import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAddNewProductMutation } from "../features/products/productsApiSlice";
import useAuth from "../hooks/useAuth";
import formatNumber, { handlePriceInput, handleWholeNumberInput } from "../utility/formatNumber";
import InputBox from "../components/InputBox";
import DropdownInput from "../components/DropdownInput";
import { productCondition, ROUTES } from "../assets/data";
import MultipleImageInput from "../components/MultipleImageInput";
import RichTextEditor from "../components/RichTextEditor";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { addToStore, updateStoreAfterAdd } from "../features/products/productSlice";
import { selectSubCategories } from "../features/subCategories/subCategorySlice";
import { selectBrands } from "../features/brands/brandSlice";
import toast from "react-hot-toast";
import PlaceholderImage from "../assets/images/placeholder.jpg";
import useBrand from "../hooks/useBrand";
import useCategory from "../hooks/useCategory";
import useSubCategory from "../hooks/useSubCategory";
import LoadingOverlay from "../components/LoadingOverlay";

const createProductSchema = (need) => {
  const baseSchema = {
    productName: yup.string().required("Product name is required"),
    coverDetail: yup.string().required("Cover detail is required"),
    description: yup.string(),
    productImages: yup.array().min(1, "At least one image is required"),
  };
  const sellSchema = {
    ...baseSchema,
    price: yup.string().required("Price is required")
      .test("is-valid-price", "Please enter a valid price", (value) => {
        if (!value) return false;
        const numericValue = value.replace(/,/g, "");
        return !isNaN(numericValue) && parseFloat(numericValue) > 0;
      }),
    brand: yup.string().required("Brand is required"),
    category: yup.string().required("Category is required"),
    condition: yup.string().required("Condition is required"),
    stock: yup.string().required("Stock is required")
      .test("is-valid-stock", "Please enter a valid stock quantity", (value) => {
        if (!value) return false;
        return !isNaN(value) && parseInt(value) > 0;
      })
  };
  const requestSchema = {
    ...baseSchema,
    price: yup.string()
      .test("is-valid-price", "Please enter a valid price", (value) => {
        if (!value) return true;
        const numericValue = value.replace(/,/g, "");
        return !isNaN(numericValue) && parseFloat(numericValue) > 0;
      }),
    brand: yup.string(),
    category: yup.string(),
    condition: yup.string(),
    stock: yup.string()
      .test("is-valid-stock", "Please enter a valid stock quantity", (value) => {
        if (!value) return true; // Empty is allowed for request
        return !isNaN(value) && parseInt(value) > 0;
      })
  };

  if (need === "Sell")  return yup.object().shape(sellSchema);
  return yup.object().shape(requestSchema);
};

const CreateProduct = () => {
  const dispatch = useDispatch();
  const { id } = useAuth();
  const [addNewProduct, { isLoading, isSuccess, isError, error }] = useAddNewProductMutation();
  const [isCreating, setIsCreating] = useState(false);
  
  // Get data from Redux store
  // const subCategories = useSelector((state) => state.subCategories.subCategories);
  const {subCategories, isLoading: subCategoryLoading, error: subCategoryError} = useSubCategory();
  // const brands = useSelector(selectBrands);
  const { brands, isLoading: brandsLoading, error: brandsError } = useBrand();
  
  console.log('SubCategories from store:', subCategories);
  console.log('Brands from store:', brands);
  
  const navigate = useNavigate();
  const [need, setNeed] = useState("Sell");
  
  // Image States
  const [productImages, setProductImages] = useState([]);
  const [iOrder, setIOrder] = useState([]);
  const [pOldImages, setPOldImages] = useState([]);

  // Initialize react-hook-form with Controller for complex inputs
  const { register, handleSubmit, formState: { errors, isSubmitted }, setValue, watch, trigger, control } = useForm({
    resolver: yupResolver(createProductSchema(need)),
    mode: "onChange",
    defaultValues: { productName: "", brand: "", condition: "", category: "", price: "", coverDetail: "", description: "", stock: "", need: "Sell", productImages: [] }
  });
  
  // Update form values when images change
  useEffect(() => {
    setValue("productImages", productImages);
    trigger("productImages"); // This will trigger validation for productImages field
  }, [productImages, setValue, trigger]);

  const onSubmit = async (data) => {
    try {
      setIsCreating(true);

      // Create a FormData object for the API request
      const formData = new FormData();
      formData.append("user", id);
      formData.append("product_name", data.productName);
      formData.append("brand", data.brand);
      formData.append("condition", data.condition);
      formData.append("description", data.description);
      formData.append("category", data.category);
      formData.append("price", data.price.replace(/,/g, ""));
      formData.append("stock", data.stock);
      formData.append("need", need);
      formData.append("cover_detail", data.coverDetail);
      formData.append("imageOrder", JSON.stringify(iOrder));

      // Create a temporary product for optimistic UI update
      const tempId = `temp-${Date.now()}`;
      
      const tempProduct = {
        _id: tempId, 
        user: id,
        product_name: data.productName,
        brand: data.brand,
        condition: data.condition,
        description: data.description,
        category: data.category,
        price: data.price,
        stock: data.stock,
        need: need,
        cover_detail: data.coverDetail,
        image: productImages.length > 0 ? [PlaceholderImage] : [],
        createdAt: new Date().toISOString()
      };

      // Optimistically add to store before the API call
      dispatch(addToStore(tempProduct));
      
      // Add product images to form data
      productImages.forEach((file) => {
        formData.append("productImages", file);
      });

      // Make the API call
      const response = await addNewProduct(formData).unwrap();
      
      // Update the store with the real product from the server
      if (response && response.product) {
        dispatch(updateStoreAfterAdd({ 
          tempId,
          serverProduct: response.product
        }));
        
        // Navigate to the store page after a short delay
        setTimeout(() => {
          navigate(ROUTES.MY_STORE);
        }, 100);
      } else {
        console.error("Invalid server response structure:", response);
        toast.error("Server response missing product data");
      }
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error(error?.data?.message || "Failed to create product");
    } finally {
      setIsCreating(false);
    }
  };

  const inputColor = "bg-slate-200";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      {isCreating && <LoadingOverlay message="Creating Product..." />}
      <div className="max-w-7xl mx-auto">
        <p className={isError ? "text-red-500 mb-4" : "hidden"}>{error?.data?.message}</p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Product Type Toggle */}
          <h2 className="text-xl py-2 px-4 border-2 border-dashed border-slate-200 rounded-lg font-semibold text-slate-400 mb-4">CREATE POST</h2>
          {/* Product Type Toggle */}
          <div className="bg-gradient-to-br from-[#a7efff] via-[#68d8f7] to-[#79dbff] p-6 rounded-lg shadow-xl">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Want to</h2>
            <div className="flex gap-2">
              <div className={`flex-1 h-12 text-base rounded-md font-semibold border-white flex items-center justify-center ${need === "Sell" ? "bg-black/70 text-neutral-300 border" : "bg-white/60 text-black border-none"}`} onClick={() => setNeed("Sell")}>Sell</div>

              <span className="text-white flex items-center justify-center">or</span>

              <div className={`flex-1 h-12 text-base rounded-md font-semibold border-white flex items-center justify-center ${need === "Request" ? "bg-black/70 text-neutral-300 border" : "bg-white/60 text-black border-none"}`} onClick={() => setNeed("Request")}>Request</div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-whit p-6 rounded-lg shadow-lg bg-slate-200">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Name */}
              <div className="space-y-1">
                <Controller name="productName" control={control} render={({ field }) => 
                  ( <InputBox usedFor="productName" boxLabel="Product Name" color={inputColor} onChange={(e) => field.onChange(e.target.value)} value={field.value} mustFill={true} /> )}
                />
                {errors.productName && <p className="text-red-500 text-sm">{errors.productName.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">Enter a clear and descriptive name</p>
              </div>
              {/* Brand */}
              <div className="space-y-1">
                <Controller name="brand" control={control} render={({ field }) => 
                  ( <DropdownInput usedFor="brand" boxLabel="Brand" color={inputColor} data={brands} onChange={(e) => field.onChange(e.target.value)} value={field.value} mustFill={need==="Sell" ? true : false} /> )}
                />
                {errors.brand && <p className="text-red-500 text-sm">{errors.brand.message}</p>}
              </div>
              {/* Category */}
              <div className="space-y-1">
                <Controller name="category" control={control} render={({ field }) => 
                  ( <DropdownInput usedFor="category" boxLabel="Category" color={inputColor} data={subCategories} onChange={(e) => field.onChange(e.target.value)} value={field.value} mustFill={need==="Sell" ? true : false} /> )}
                />
                {errors.category && <p className="text-red-500 text-sm">{errors.category.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">Choose the most appropriate category</p>
              </div>
              {/* Condition */}
              <div className="space-y-1">
                <Controller name="condition" control={control} render={({ field }) => 
                  ( <DropdownInput usedFor="condition" boxLabel="Condition" color={inputColor} data={productCondition} onChange={(e) => field.onChange(e.target.value)} value={field.value} mustFill={need==="Sell" ? true : false} /> )}
                />
                {errors.condition && <p className="text-red-500 text-sm">{errors.condition.message}</p>}
              </div>
            </div>
          </div>

          {/* Pricing and Stock */}
          <div className="bg-slate-200 p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Pricing and {need==="Request" ? "Amount" : "Stock"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <Controller name="price" control={control} render={({ field }) => 
                  (<InputBox usedFor="price" boxLabel="Price" color={inputColor} onChange={(e) => {
                    const processedValue = handlePriceInput(e.target.value);
                    field.onChange(processedValue);
                  }} value={field.value ? formatNumber(field.value) : ''} mustFill={true} />)}
                />
                {errors.price && <p className="text-red-500 text-sm">{errors.price.message}</p>}
              </div>

              <div className="space-y-1">
                <Controller name="stock" control={control} render={({ field }) => 
                  (<InputBox usedFor="stock" boxLabel={need==="Request" ? "Amount" : "Stock"} color={inputColor} onChange={(e) => {
                    const processedValue = handleWholeNumberInput(e.target.value);
                    field.onChange(processedValue);
                  }} value={field.value ? formatNumber(field.value) : ''} mustFill={need==="Sell" ? true : false} />)}
                />
                {errors.stock && <p className="text-red-500 text-sm">{errors.stock.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">{need==="Request" ? "Enter the amount you want to request" : "Number of items available for sale"}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-slate-200 p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Description</h2>
            <div className="space-y-6">
              <div className="space-y-1">
                <Controller name="description" control={control}
                  render={({ field }) => (
                    <RichTextEditor value={field.value} onChange={field.onChange} className="w-full" />
                  )}
                />
                {errors.description && <p className="text-red-500 text-sm">{errors.description.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">Provide detailed information about your product. Include features, specifications, and any important details.</p>
              </div>

              <div className="space-y-1">
                <Controller name="coverDetail" control={control} render={({ field }) => (
                  <InputBox usedFor="coverDetail" boxLabel="Cover Detail" color={inputColor} onChange={(e) => field.onChange(e.target.value)} value={field.value} mustFill={true} /> )}
                />
                {errors.coverDetail && <p className="text-red-500 text-sm">{errors.coverDetail.message}</p>}
                <p className="text-[.9rem] text-gray-400 italic">Write a sentence long cover detail for thumbnail</p>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-slate-200 p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Product Images</h2>
            <div className="space-y-1">
              <MultipleImageInput pOldImages={pOldImages} setPOldImages={setPOldImages} setProductImages={setProductImages} iOrder={iOrder} setIOrder={setIOrder} />
              {isSubmitted && errors.productImages && (
                <p className="text-red-500 text-sm">{errors.productImages.message}</p>
              )}
              <p className="text-[.9rem] text-gray-400 italic">Upload 1-5 clear images of your product. First image will be used as the cover image.</p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button 
              type="submit" 
              disabled={isCreating} 
              className={`w-full md:w-[140px] h-12 bg-neutral-900 text-white text-[1.1rem] rounded-md transition-opacity ${
                isCreating ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Post Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProduct;